import { useConnection } from "@solana/wallet-adapter-react";
import type { Connection } from "@solana/web3.js";
import { asyncSettlePromises, chunks, fromLamports, NATIVE_SOL } from "@stepfinance/common";
import type { NormalizedTokenAccount } from "@stepfinance/tokens";
import { useQuery } from "@tanstack/react-query";

import { useAccountsContext } from "../../../contexts/accounts";
import { useMarkets } from "../../../contexts/market";
import type { MagicEdenCollectionStats } from "../../../models/magiceden/types";
import type { NFTWithTokenAccount } from "../../../models/nfts/nfts";
import { SA_RESOURCE_TOKEN_MINTS } from "../../../models/staratlas/tokens";
import { fetchNFTsFromTokenAccounts } from "../../../utils/nft/load";
import { getMECollectionStatByMint } from "../../../utils/nft/utils";
import { STEP_API_ENDPOINT } from "../../../utils/swr";
import { fetchJSONFromUrl } from "../../../utils/utils";
import { useConnectedWallet } from "../../wallet";
import { queryConfig } from "./../queryConfig";

type NFTQueryData = {
  nftMap: Map<string, NFTWithTokenAccount>;
  totalValue: number;
  collectionList: Set<string>;
};

export const useNFTsWithTokensAndPricesByOwner = () => {
  const wallet = useConnectedWallet();
  const { connection } = useConnection();
  const {
    parsedUserTokenAccounts: { zeroDecimalTokenAccounts },
  } = useAccountsContext();
  const { priceInUSD, getSerumMarketPrices, marketsInitialised } = useMarkets();

  return useQuery<NFTQueryData>(
    ["nftsWithTokensAndPrices", wallet?.publicKey.toBase58()],
    async () => {
      // Fetch NFTs and ME price info
      const [nftMap, magicEdenResponse] = await asyncSettlePromises([
        fetchNFTsWithTokenAccountsByOwner(connection, zeroDecimalTokenAccounts),
        fetchMagicEdenNFTStatsByOwner(wallet?.publicKey.toBase58()),
      ]);

      let totalValue = 0;
      const collectionList: Set<string> = new Set();

      if (!nftMap) {
        return { nftMap: new Map(), totalValue, collectionList };
      }
      // If ME endpoint fails, return collections and NFTs without pricing
      if (!magicEdenResponse) {
        nftMap.forEach((nft) => nft.collectionName && collectionList.add(nft.collectionName));
        return { nftMap, totalValue, collectionList };
      }

      const solPriceUSD = priceInUSD(NATIVE_SOL.toBase58()) ?? 0;

      // Apply prices to NFTs, populate collections list, and calc total value
      nftMap.forEach((nft, mint) => {
        nft.collectionName && collectionList.add(nft.collectionName);
        const collectionStats = getMECollectionStatByMint(mint, magicEdenResponse);
        if (collectionStats?.floorPrice) {
          const floorPrice = fromLamports(collectionStats?.floorPrice, 9);
          const floorPriceUSD = floorPrice * solPriceUSD;
          totalValue += floorPriceUSD;
          nftMap.set(mint, { ...nft, floorPrice, floorPriceUSD });
        } else {
          const price = getSerumMarketPrices(mint).bid;
          if (price !== null) {
            const floorPriceUSD = price;
            const floorPrice = floorPriceUSD / solPriceUSD;
            totalValue += floorPriceUSD;
            nftMap.set(mint, { ...nft, floorPrice, floorPriceUSD });
          }
        }
      });
      return { nftMap, totalValue, collectionList };
    },
    {
      ...queryConfig,
      enabled: !!wallet && !!marketsInitialised && zeroDecimalTokenAccounts.length > 0,
      staleTime: 1000 * 120,
    },
  );
};

const fetchNFTsWithTokenAccountsByOwner = async (connection: Connection, tokenAccounts: NormalizedTokenAccount[]) => {
  const CHUNK_SIZE = 50;

  const filteredTokenAccounts = tokenAccounts.filter(
    (acct) => !SA_RESOURCE_TOKEN_MINTS.some((mint) => mint === acct.info.mint),
  );
  const filteredTokenAccountChunks = chunks(filteredTokenAccounts, CHUNK_SIZE);

  const allNFTs = (
    await asyncSettlePromises(filteredTokenAccountChunks.map((chunk) => fetchNFTsFromTokenAccounts(connection, chunk)))
  )
    .filter((x): x is NFTWithTokenAccount[] => !!x)
    .flat();

  const nftMap = new Map(allNFTs.map((nft) => [nft.address.toBase58(), nft]));

  return nftMap;
};

const fetchMagicEdenNFTStatsByOwner = async (walletAddress?: string) => {
  const data: MagicEdenCollectionStats[] = await fetchJSONFromUrl(
    `${STEP_API_ENDPOINT}/v1/integration/magic_eden_collection_pricing?address=${walletAddress}`,
  );
  return data;
};
